import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Pool, PoolTeam } from '../../types/pool';

interface KnockoutEntry {
  id: string;
  entryName: string;
  userId: string;
  username?: string;
  displayName: string;
  usedTeams: number;
  buyBackCount: number;
  eliminatedOn: string | null;
}

interface KnockoutStatus {
  poolId: string;
  poolName: string;
  summary: {
    total: number;
    alive: number;
    eliminated: number;
    boughtBack: number;
  };
  entries: {
    ALIVE: KnockoutEntry[];
    ELIMINATED: KnockoutEntry[];
    BOUGHT_BACK: KnockoutEntry[];
  };
}

interface TeamResult {
  teamId: string;
  teamName: string;
  won: boolean;
}

interface GameDay {
  date: string;
  label: string;
  round: string;
  bracketDay: number | null;
}

function deriveBracketDay(eventName: string): number | null {
  if (/day\s*1/i.test(eventName)) return 1;
  if (/day\s*2/i.test(eventName)) return 2;
  return null;
}

function buildGameDays(events: any[]): GameDay[] {
  if (!events || events.length === 0) return [];
  return events
    .map((evt: any) => {
      const date = new Date(evt.scheduledAt).toISOString().split('T')[0];
      const roundKey = evt.name || evt.round || '';
      return {
        date,
        label: roundKey,
        round: evt.round || roundKey,
        bracketDay: deriveBracketDay(roundKey),
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function AdminKnockoutResults() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently } = useAuth0();

  const [pool, setPool] = useState<Pool | null>(null);
  const [knockoutStatus, setKnockoutStatus] = useState<KnockoutStatus | null>(null);
  const [teams, setTeams] = useState<PoolTeam[]>([]);
  const [gameDays, setGameDays] = useState<GameDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Results entry state
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [teamResults, setTeamResults] = useState<TeamResult[]>([]);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<string | null>(null);

  // Buy-back state
  const [buyingBack, setBuyingBack] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!poolId) return;

    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();

      // Load pool details
      const poolData = await api.getPool(poolId);
      setPool(poolData);
      setTeams(poolData.teams || []);

      // Load events/schedule for game day selector
      try {
        const eventsData = await api.getPoolEventsPublic(poolId);
        const days = buildGameDays(eventsData.events || []);
        setGameDays(days);

        // Default to the first upcoming game day
        if (!selectedDate) {
          const today = new Date().toISOString().split('T')[0];
          const upcomingDay = days.find(d => d.date >= today);
          if (upcomingDay) {
            setSelectedDate(upcomingDay.date);
          } else if (days.length > 0) {
            setSelectedDate(days[0].date);
          }
        }
      } catch {
        // No events loaded
      }

      // Load knockout status
      const status = await api.getKnockoutStatus(poolId, token);
      setKnockoutStatus(status);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [poolId, getAccessTokenSilently]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function toggleTeamResult(teamId: string, teamName: string) {
    setTeamResults(prev => {
      const existing = prev.find(r => r.teamId === teamId);
      if (existing) {
        // Toggle between won, lost, and remove
        if (existing.won) {
          // Change from won to lost
          return prev.map(r => r.teamId === teamId ? { ...r, won: false } : r);
        } else {
          // Remove from results
          return prev.filter(r => r.teamId !== teamId);
        }
      } else {
        // Add as won
        return [...prev, { teamId, teamName, won: true }];
      }
    });
  }

  function getTeamResultStatus(teamId: string): 'won' | 'lost' | 'none' {
    const result = teamResults.find(r => r.teamId === teamId);
    if (!result) return 'none';
    return result.won ? 'won' : 'lost';
  }

  async function handleProcessResults() {
    if (!poolId || teamResults.length === 0) return;

    try {
      setProcessing(true);
      setProcessResult(null);
      const token = await getAccessTokenSilently();

      const result = await api.processKnockoutResults(
        poolId,
        { date: selectedDate, results: teamResults },
        token,
      );
      setProcessResult(result.message);

      // Reload status
      await loadData();

      // Clear team results
      setTeamResults([]);
    } catch (err) {
      console.error('Failed to process results:', err);
      setProcessResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setProcessing(false);
    }
  }

  async function handleBuyBack(entryId: string) {
    if (!poolId) return;

    try {
      setBuyingBack(entryId);
      const token = await getAccessTokenSilently();

      await api.processKnockoutBuyBack(poolId, entryId, token);

      // Reload status
      await loadData();
    } catch (err) {
      console.error('Failed to process buy-back:', err);
      setError(err instanceof Error ? err.message : 'Failed to process buy-back');
    } finally {
      setBuyingBack(null);
    }
  }

  // Hooks must be called before any early returns
  const currentGameDay = useMemo(
    () => gameDays.find(d => d.date === selectedDate),
    [gameDays, selectedDate]
  );

  const filteredTeams = useMemo(() => {
    if (!currentGameDay?.bracketDay) return teams;
    return teams.filter(pt => {
      const teamBracketDay = pt.metadata?.bracketDay;
      return teamBracketDay == null || teamBracketDay === currentGameDay.bracketDay;
    });
  }, [teams, currentGameDay]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !pool || !knockoutStatus) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error || 'Failed to load pool'}</p>
          <Link to="/admin" className="mt-3 inline-block text-sm text-blue-600 hover:underline">
            ← Back to Admin Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Group filtered teams by region
  const teamsByRegion: Record<string, PoolTeam[]> = {};
  for (const team of filteredTeams) {
    const region = team.metadata?.region || 'Other';
    if (!teamsByRegion[region]) {
      teamsByRegion[region] = [];
    }
    teamsByRegion[region].push(team);
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link to="/admin" className="text-blue-600 hover:underline text-sm">
          ← Back to Admin Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">{pool.name}</h1>
        <p className="text-gray-600">Knockout Pool Management</p>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Total Entries</div>
          <div className="text-2xl font-bold text-gray-900">{knockoutStatus.summary.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-green-600">Alive</div>
          <div className="text-2xl font-bold text-green-600">{knockoutStatus.summary.alive}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-red-600">Eliminated</div>
          <div className="text-2xl font-bold text-red-600">{knockoutStatus.summary.eliminated}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-orange-600">Bought Back</div>
          <div className="text-2xl font-bold text-orange-600">{knockoutStatus.summary.boughtBack}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Enter Results Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Enter Daily Results</h2>
            <p className="text-sm text-gray-600 mt-1">Select teams that won or lost today</p>
          </div>

          <div className="px-6 py-4">
            {/* Game Day Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Game Day
              </label>
              {gameDays.length === 0 ? (
                <div className="mb-2">
                  <p className="text-sm text-gray-500 mb-2">No schedule imported. Using manual date:</p>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {gameDays.map((day) => (
                    <button
                      key={day.date}
                      type="button"
                      onClick={() => { setSelectedDate(day.date); setTeamResults([]); }}
                      className={`p-2 rounded-lg border-2 text-left transition-all text-sm ${
                        selectedDate === day.date
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white hover:border-gray-400'
                      }`}
                    >
                      <p className="text-xs text-gray-500">
                        {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                      <p className="font-semibold text-gray-900 truncate">{day.label}</p>
                    </button>
                  ))}
                </div>
              )}
              {currentGameDay && (
                <p className="text-sm text-gray-600">
                  Showing teams for <strong>{currentGameDay.label}</strong>
                  {currentGameDay.bracketDay ? ` (Bracket Day ${currentGameDay.bracketDay})` : ' (all teams)'}
                </p>
              )}
            </div>

            {/* Team Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team Results
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Click once = Won (green), Click twice = Lost (red), Click third = Remove
              </p>

              {Object.entries(teamsByRegion).map(([region, regionTeams]) => (
                <div key={region} className="mb-4">
                  <h4 className="text-sm font-medium text-gray-600 mb-2">{region}</h4>
                  <div className="flex flex-wrap gap-2">
                    {regionTeams.sort((a, b) => (a.seed || 16) - (b.seed || 16)).map((team) => {
                      const status = getTeamResultStatus(team.team.id);
                      return (
                        <button
                          key={team.team.id}
                          onClick={() => toggleTeamResult(team.team.id, team.team.name)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            status === 'won'
                              ? 'bg-green-100 text-green-800 border-2 border-green-500'
                              : status === 'lost'
                              ? 'bg-red-100 text-red-800 border-2 border-red-500'
                              : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                          }`}
                        >
                          ({team.seed}) {team.team.shortName || team.team.name}
                          {status === 'won' && ' ✓'}
                          {status === 'lost' && ' ✗'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Results Summary */}
            {teamResults.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Results:</h4>
                <div className="space-y-1 text-sm">
                  {teamResults.filter(r => r.won).map(r => (
                    <div key={r.teamId} className="text-green-700">✓ {r.teamName} - Won</div>
                  ))}
                  {teamResults.filter(r => !r.won).map(r => (
                    <div key={r.teamId} className="text-red-700">✗ {r.teamName} - Lost</div>
                  ))}
                </div>
              </div>
            )}

            {/* Process Button */}
            <button
              onClick={handleProcessResults}
              disabled={processing || teamResults.length === 0}
              className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing && (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {processing ? 'Processing...' : 'Process Results'}
            </button>

            {/* Result Message */}
            {processResult && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${
                processResult.startsWith('Error')
                  ? 'bg-red-50 text-red-800'
                  : 'bg-green-50 text-green-800'
              }`}>
                {processResult}
              </div>
            )}
          </div>
        </div>

        {/* Entries by Status */}
        <div className="space-y-6">
          {/* Alive Entries */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
              <h3 className="text-lg font-semibold text-green-800">
                Alive Entries ({knockoutStatus.entries.ALIVE.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
              {knockoutStatus.entries.ALIVE.length === 0 ? (
                <div className="px-6 py-4 text-gray-500 text-sm">No entries alive</div>
              ) : (
                knockoutStatus.entries.ALIVE.map((entry) => (
                  <div key={entry.id} className="px-6 py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{entry.entryName}</div>
                      <div className="text-sm text-gray-500">{entry.displayName}</div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {entry.usedTeams} teams used
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Bought Back Entries */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 bg-orange-50">
              <h3 className="text-lg font-semibold text-orange-800">
                Bought Back ({knockoutStatus.entries.BOUGHT_BACK.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
              {knockoutStatus.entries.BOUGHT_BACK.length === 0 ? (
                <div className="px-6 py-4 text-gray-500 text-sm">No bought back entries</div>
              ) : (
                knockoutStatus.entries.BOUGHT_BACK.map((entry) => (
                  <div key={entry.id} className="px-6 py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{entry.entryName}</div>
                      <div className="text-sm text-gray-500">{entry.displayName}</div>
                    </div>
                    <div className="text-sm text-orange-600">
                      {entry.buyBackCount}/3 buy-backs used
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Eliminated Entries */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
              <h3 className="text-lg font-semibold text-red-800">
                Eliminated ({knockoutStatus.entries.ELIMINATED.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {knockoutStatus.entries.ELIMINATED.length === 0 ? (
                <div className="px-6 py-4 text-gray-500 text-sm">No eliminated entries</div>
              ) : (
                knockoutStatus.entries.ELIMINATED.map((entry) => (
                  <div key={entry.id} className="px-6 py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{entry.entryName}</div>
                      <div className="text-sm text-gray-500">
                        {entry.displayName} • Eliminated: {entry.eliminatedOn}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {entry.buyBackCount}/3 buy-backs
                      </span>
                      {entry.buyBackCount < 3 && (
                        <button
                          onClick={() => handleBuyBack(entry.id)}
                          disabled={buyingBack === entry.id}
                          className="px-3 py-1 bg-orange-100 hover:bg-orange-200 text-orange-800 text-sm font-medium rounded transition-colors disabled:opacity-50"
                        >
                          {buyingBack === entry.id ? 'Processing...' : 'Buy Back'}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
